import { matchProcessMasterOption } from "../pages/products/productProcessMigrationUtils";
import { resolveTaxonomyDefinitions } from "./taxonomyApi";
import type { ProcessFieldMapping, ResolvedFieldConfig, TaxonomyValues } from "./types";
import { eligibleForContext, isEmptyTaxonomyValue, missingRequiredOnPublish } from "./values";

export type RequiredField = ResolvedFieldConfig & { focusId?: string };
type ProcessSlot = { key: string; title: string; processMasterId?: string; taxonomyValues?: TaxonomyValues };
type ProcessMaster = { value: string; label: string; taxonomyIds?: string[]; mappings?: ProcessFieldMapping[] };

export function fieldsForProcess(slot: ProcessSlot, fields: ResolvedFieldConfig[], masters: ProcessMaster[]) {
  const master = slot.processMasterId
    ? masters.find((row) => row.value === slot.processMasterId)
    : matchProcessMasterOption(masters, slot.title);
  const hasAuthoritativeMappings = Array.isArray(master?.mappings);
  const mappings = (master?.mappings || []).filter((row) => row.enabled);
  const ids = hasAuthoritativeMappings ? mappings.map((row) => row.taxonomyId) : (master?.taxonomyIds || []);
  return fields.filter((field) => field.enabled && eligibleForContext(field.usedIn, "PROCESS")
    && ids.includes(field.taxonomyId)).map((field) => {
      const mapping = mappings.find((row) => row.taxonomyId === field.taxonomyId);
      return { ...field, requiredOnPublish: false, requiredInProcess: mapping?.requiredInProcess === true };
    });
}

export function missingRequiredInProcess(fields: ResolvedFieldConfig[], values?: TaxonomyValues) {
  return fields
    .filter((field) => field.enabled && field.requiredInProcess)
    .filter((field) => isEmptyTaxonomyValue((values || {})[field.taxonomyId]));
}

export function processFieldId(slotKey: string, taxonomyId: string) {
  return `hkp-tax-process-${slotKey}-${taxonomyId}`;
}

export function missingPublishFields(
  productFields: ResolvedFieldConfig[], values: TaxonomyValues | undefined,
  processFields: ResolvedFieldConfig[], slots: ProcessSlot[], masters: ProcessMaster[]
): RequiredField[] {
  return [
    ...missingRequiredOnPublish(productFields, values),
    ...(slots || []).flatMap((slot, index) =>
      missingRequiredInProcess(fieldsForProcess(slot, processFields, masters), slot.taxonomyValues)
        .map((field) => ({ ...field, name: `${slot.title} (${index + 1}): ${field.name}`,
          focusId: processFieldId(slot.key, field.taxonomyId) }))),
  ];
}

export async function resolvePublishFailure(
  ids: string[], productFields: ResolvedFieldConfig[], values: TaxonomyValues | undefined,
  processFields: ResolvedFieldConfig[], slots: ProcessSlot[], masters: ProcessMaster[]
): Promise<RequiredField[]> {
  const known = missingPublishFields(productFields, values, processFields, slots, masters)
    .filter((field) => ids.includes(field.taxonomyId));
  const unresolved = ids.filter((id) => !known.some((field) => field.taxonomyId === id));
  if (!unresolved.length) return known;
  const response = await resolveTaxonomyDefinitions(unresolved);
  return [...known, ...unresolved.map((id): RequiredField => {
    const field = response.data?.find((row) => row.id === id);
    const active = [...productFields, ...processFields].find((row) => row.taxonomyId === id);
    return { taxonomyId: id, name: field?.name || active?.name || id,
      fieldType: field?.fieldType || active?.fieldType || "TEXT",
      usedIn: field?.usedIn || active?.usedIn || "PRODUCT", source: field?.source || "STUDIO",
      enabled: true, requiredOnPublish: true, displayOrder: 0,
      focusId: "hkp-product-attributes" };
  })];
}
